import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button, Card, StatusPill } from "@/components/kit";

const TYPE = { resident: "Resident", nextgen_youth: "Next Gen Youth", business: "Business", organization: "Organization", church: "Church" };
const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

function MembershipCard({ m, onPaid }) {
  const [busy, setBusy] = useState(false);
  const cert = async () => {
    try {
      const r = await api.get(`/cards/my-certificate/${m.member_id}.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `rosemoor-certificate-${m.member_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download certificate"); }
  };
  const pay = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/membership/my-memberships/${m.member_id}/pay`, {});
      if (!r.data.paid) toast.info(r.data.message); else { toast.success("Payment received"); onPaid(); }
    }
    catch (e) { toast.error(e.response?.data?.detail || "Payment failed"); }
    setBusy(false);
  };
  return (
    <Card className="p-5" data-testid={`membership-card-${m.member_number}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest font-bold text-dark-olive">{m.is_personal ? "My membership" : `${TYPE[m.account_type]} I represent`}</div>
          <div className="font-zilla text-xl font-semibold truncate">{m.name}</div>
          <div className="text-[14px] text-ink/65"><span className="font-mono">{m.member_number}</span> · {TYPE[m.account_type] || m.account_type}</div>
        </div>
        <StatusPill tone={m.status === "active" ? "olive" : "maroon"} className="capitalize">{m.status}</StatusPill>
      </div>
      <div className="mt-3 text-[15px]" data-testid={`membership-dues-${m.member_number}`}>
        {m.included_with
          ? <span className="text-dark-olive font-semibold">Included with {m.included_with}</span>
          : <span>{m.amount_cents ? `${money(m.amount_cents)} ${m.cadence || ""}` : "No dues"}{m.renews_on ? ` · renews ${m.renews_on}` : ""}</span>}
      </div>
      {m.unpaid && (
        <div className="mt-3 bg-maroon/8 border border-maroon/25 rounded-lg px-3 py-2 text-[14px] text-maroon flex items-center justify-between gap-3" data-testid={`membership-unpaid-${m.member_number}`}>
          <span>This membership's dues are unpaid.</span>
          <Button onClick={pay} disabled={busy} className="min-h-[36px] py-1.5" data-testid={`membership-pay-${m.member_number}`}>{busy ? "Paying…" : "Pay now"}</Button>
        </div>
      )}
      {m.has_certificate && <Button variant="secondary" onClick={cert} className="mt-3" data-testid={`membership-cert-${m.member_number}`}>Download certificate</Button>}
    </Card>
  );
}

export default function MyMemberships() {
  const [list, setList] = useState(null);
  const load = () => api.get("/membership/my-memberships").then((r) => setList(r.data.memberships)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  if (!list || list.length === 0) return null;
  return (
    <div className="mt-8 max-w-2xl" data-testid="my-memberships">
      <h2 className="font-zilla text-2xl font-semibold mb-1">My memberships</h2>
      <p className="text-[15px] text-ink/65 mb-4">You pay for one membership. The others you hold are included at no charge.</p>
      <div className="space-y-3">{list.map((m) => <MembershipCard key={m.member_id} m={m} onPaid={load} />)}</div>
    </div>
  );
}
